import React, { useState } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Checkbox, 
    FormControlLabel,
    IconButton,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import SaveIcon from '@mui/icons-material/Save';

const FIXSessionConfig = ({ open, onClose, lp }) => {
    const [formData, setFormData] = useState({
        beginString: 'FIX.4.4',
        heartbeatInterval: '30',
        senderCompId: 'QUANTX_TRADER',
        targetCompId: lp ? `${lp.name.toUpperCase()}_LP` : 'CENTROID_LP',
        host: lp ? `fix.${lp.name.toLowerCase()}.com` : 'fix.centroid.com',
        port: '9876',
        useSSL: true,
        connectionType: 'Initiator'
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleTestConnection = () => {
        // Handle test connection logic
        console.log('Testing connection with:', formData);
    };

    const handleSave = () => {
        // Handle save logic
        console.log('Saving configuration:', formData);
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Box sx={{
                width: '90vw',
                maxWidth: 600,
                maxHeight: '90vh',
                background: 'rgba(24, 31, 44, 0.95)',
                borderRadius: 2,
                border: '1px solid rgba(6, 182, 212, 0.3)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SettingsIcon sx={{ color: '#fff', fontSize: 24 }} />
                        <Typography variant="h5" sx={{ color: '#1de9b6', fontWeight: 700 }}>
                            FIX Session Configuration - {lp?.name?.toUpperCase() || 'CENTROID'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Info Banner */}
                <Alert 
                    severity="info" 
                    icon={<InfoIcon />}
                    sx={{
                        background: '#1976d2',
                        color: '#fff',
                        '& .MuiAlert-icon': { color: '#fff' },
                        borderRadius: 0
                    }}
                >
                    Configure FIX protocol session settings for secure LP connection
                </Alert>

                {/* Content */}
                <Box sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
                    {/* Basic Settings */}
                    <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
                        Basic Settings
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: '#bfc9db' }}>Begin String</InputLabel>
                            <Select
                                value={formData.beginString}
                                onChange={(e) => handleInputChange('beginString', e.target.value)}
                                sx={{
                                    color: '#fff',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(6, 182, 212, 0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(6, 182, 212, 0.5)'
                                    }
                                }}
                            >
                                <MenuItem value="FIX.4.4">FIX.4.4</MenuItem>
                                <MenuItem value="FIX.4.2">FIX.4.2</MenuItem>
                                <MenuItem value="FIX.4.1">FIX.4.1</MenuItem>
                            </Select>
                        </FormControl>
                        
                        <TextField
                            label="Heartbeat Interval (seconds)"
                            value={formData.heartbeatInterval}
                            onChange={(e) => handleInputChange('heartbeatInterval', e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiInputLabel-root': { color: '#bfc9db' },
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' }
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                        <TextField
                            label="Sender Comp ID"
                            value={formData.senderCompId}
                            onChange={(e) => handleInputChange('senderCompId', e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiInputLabel-root': { color: '#bfc9db' },
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' }
                                }
                            }}
                        />
                        
                        <TextField
                            label="Target Comp ID"
                            value={formData.targetCompId}
                            onChange={(e) => handleInputChange('targetCompId', e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiInputLabel-root': { color: '#bfc9db' },
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' }
                                }
                            }}
                        />
                    </Box>

                    {/* Connection Settings */}
                    <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
                        Connection Settings
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                        <TextField
                            label="Host"
                            value={formData.host}
                            onChange={(e) => handleInputChange('host', e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiInputLabel-root': { color: '#bfc9db' },
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' }
                                }
                            }}
                        />
                        
                        <TextField
                            label="Port"
                            value={formData.port}
                            onChange={(e) => handleInputChange('port', e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiInputLabel-root': { color: '#bfc9db' },
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' }
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.useSSL}
                                    onChange={(e) => handleInputChange('useSSL', e.target.checked)}
                                    sx={{
                                        color: '#1de9b6',
                                        '&.Mui-checked': { color: '#1de9b6' }
                                    }}
                                />
                            }
                            label="Use SSL/TLS"
                            sx={{ color: '#bfc9db' }}
                        />
                        
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: '#bfc9db' }}>Connection Type</InputLabel>
                            <Select
                                value={formData.connectionType}
                                onChange={(e) => handleInputChange('connectionType', e.target.value)}
                                sx={{
                                    color: '#fff',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(6, 182, 212, 0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(6, 182, 212, 0.5)'
                                    }
                                }}
                            >
                                <MenuItem value="Initiator">Initiator</MenuItem>
                                <MenuItem value="Acceptor">Acceptor</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Session Management */}
                    <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
                        Session Management
                    </Typography>
                    {/* Additional session management fields can be added here */}
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}>
                    <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={onClose}
                        sx={{
                            color: '#ff6b6b',
                            borderColor: '#ff6b6b',
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={handleTestConnection}
                        sx={{
                            color: '#00cfff',
                            borderColor: '#00cfff',
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Test Connection
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        sx={{
                            background: '#1de9b6',
                            color: '#000',
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#19b89b'
                            }
                        }}
                    >
                        Save Configuration
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default FIXSessionConfig; 